import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import StoreLogoUpload from "./Stores/StoreLogoUpload";
import ShareStoreDialog from "../components/ProductGroups/ShareStoreDialog";
import IconButton from "@mui/material/IconButton";
import IosShareIcon from "@mui/icons-material/IosShare";
import RefreshIcon from '@mui/icons-material/Refresh';
import useFetch from "../hooks/useFetch";
import { useCurrentStore } from "../hooks/useCurrentStore";
import StoreSelector from "./StoreSelector";
import "./DashboardSidebar.css";

let _commissionFetched = false;

interface Props { commissionData?: any; }
const DashboardSidebar: React.FC<Props> = ({ commissionData }) => {
  
  const getUser = () => JSON.parse(localStorage.getItem("user") || "{}");
  const getStore = (id?: string | null) => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const stores = user?.stores ?? [];
    if (id) return stores.find((s: any) => String(s.id) === id) ?? stores[0] ?? null;
    const savedId = localStorage.getItem("currentStoreId");
    return stores.find((s: any) => String(s.id) === savedId) ?? stores[0] ?? null;
  };

  const [storeData, setStoreData] = useState<any>(() => getStore());
  const [rawLogo, setRawLogo] = useState<string | null>(() => getStore()?.logo ?? null);

  const StoreName = storeData?.name?.split(' ').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  const StoreId = storeData?.id;
  const currentStoreId = useCurrentStore();

  const { fetchData: fetchCommission, isLoading: _refreshing, error } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/stores/${StoreId}/commission`,
    method: "PUT",
  });

  useEffect(() => {
    if (!commissionData) return;
    const { commission_earned, commission_rate, commission_outstanding, commission_paid, commission_refunded, payment_state, store_name, store_image, username, status  } = commissionData?.currentStore ?? commissionData;

    setStoreData((prev: any) => prev ? {
      ...prev, commission_earned, commission_rate, commission_outstanding, commission_paid, commission_refunded, payment_state, name: store_name, logo: store_image, status,
    } : prev);

    if (store_image !== rawLogo) setRawLogo(store_image);

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const storeIdx = user.stores?.findIndex((s: any) => String(s.id) === String(StoreId));
    if (storeIdx !== -1 && user.stores?.[storeIdx]) {
      Object.assign(user.stores[storeIdx], { commission_earned, commission_rate, commission_outstanding, commission_paid, commission_refunded, payment_state, name: store_name, logo: store_image, status });
      if (user.first_name !== undefined && username) {
        const [first, ...rest] = username.split(" ");
        user.first_name = first;
        user.last_name = rest.join(" ");
      }
      localStorage.setItem("user", JSON.stringify(user));
    }
  }, [commissionData]);

  useEffect(() => {
    const fresh = getStore(currentStoreId);
    setStoreData(fresh);
    setRawLogo(fresh?.logo ?? null);
  }, [currentStoreId]);

useEffect(() => {
  if (StoreId && !_commissionFetched) {
    _commissionFetched = true;
    isAutoFetchRef.current = true;
    handleRefreshCommission();
  }
}, [StoreId]);

useEffect(() => {
  _commissionFetched = false;
}, [currentStoreId]);

useEffect(() => {
  const sync = () => {
    const fresh = getStore(localStorage.getItem("currentStoreId"));
    setStoreData(fresh);
    setRawLogo(fresh?.logo ?? null);
  };
  window.addEventListener("user-updated", sync);
  return () => window.removeEventListener("user-updated", sync);
}, []);

  const handleLogoChange = (newLogo: string | null) => {
    setRawLogo(newLogo);
    setStoreData((prev: any) => prev ? { ...prev, logo: newLogo } : prev);

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const storeIdx = user.stores?.findIndex((s: any) => String(s.id) === String(StoreId));
    if (storeIdx !== -1 && user.stores?.[storeIdx]) {
      user.stores[storeIdx].logo = newLogo;
      localStorage.setItem("user", JSON.stringify(user));
    }
  };

  const user = getUser();

  const currentUserName = [user.first_name, user.last_name]
    .filter(Boolean) // ensures both exist (removes null/undefined/empty)
    .map(name => name.charAt(0).toUpperCase() + name.slice(1).toLowerCase())
    .join(' ');
    
  const currentUserRole = getUser().current_role;
  
  const storeUrl = `${import.meta.env.VITE_STORE_BASE_URL}${StoreId}`;
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const [spinState, setSpinState] = useState<"idle" | "spinning" | "done">("idle");
  const spinStateRef = useRef<"idle" | "spinning" | "done">("idle");
const setSpinSync = useRef((s: "idle" | "spinning" | "done") => {
  spinStateRef.current = s;
  setSpinState(s);
}).current;
  const spinStartRef = useRef<number>(0);
  const MIN_SPIN_MS = 900;
  const prevCommissionRef = useRef<number | null>(null);
  const isAutoFetchRef = useRef(false);

  const handleRefreshCommission = async () => {
    if (!StoreId || spinStateRef.current === "spinning") return;

    if (!isAutoFetchRef.current) {
      setSpinSync("spinning");
    }
    isAutoFetchRef.current = false;
    spinStartRef.current = Date.now();

    const result = await fetchCommission();

      if (result?.success) {
        const { commission_earned, commission_rate, commission_outstanding, commission_paid, commission_refunded, payment_state, store_name, store_image, username, status } = result.data.currentStore;

        setStoreData((prev: any) => prev ? {
          ...prev,
          commission_earned,
          commission_rate,
          commission_outstanding,
          commission_paid,
          commission_refunded,
          name: store_name,
          logo: store_image,
          status,
          payment_state,
        } : prev);

        if (store_image !== rawLogo) setRawLogo(store_image);

        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const storeIdx = user.stores?.findIndex((s: any) => String(s.id) === String(StoreId));
        if (storeIdx !== -1 && user.stores?.[storeIdx]) {
          Object.assign(user.stores[storeIdx], {
            commission_earned, commission_rate, commission_outstanding, commission_paid, commission_refunded, payment_state, name: store_name, logo: store_image, status,
          });
          if (user.first_name !== undefined && username) {
            const [first, ...rest] = username.split(" ");
            user.first_name = first;
            user.last_name = rest.join(" ");
          }
          localStorage.setItem("user", JSON.stringify(user));
          const { allStores } = result.data;
          if (allStores?.length && user.stores) {
            allStores.forEach((freshStore: any) => {
              const idx = user.stores.findIndex((s: any) => String(s.id) === String(freshStore.id));
              if (idx !== -1) {
                user.stores[idx] = { ...user.stores[idx], name: freshStore.name, status: freshStore.status };
              }
            });
            localStorage.setItem("user", JSON.stringify(user));
            window.dispatchEvent(new Event("user-updated"));
          }
        }
    }

    const elapsed = Date.now() - spinStartRef.current;
    const remaining = Math.max(0, MIN_SPIN_MS - elapsed);

    setTimeout(() => {
      if (result?.success) {
        const { commission_earned } = result.data.currentStore;
        const prev = prevCommissionRef.current;
        const next = parseFloat(commission_earned ?? 0);
        prevCommissionRef.current = next;

        const currentSpinState = spinStateRef.current as string;
        if (currentSpinState === "spinning" && prev !== null && next > prev) {
          setSpinSync("done");
          setTimeout(() => setSpinSync("idle"), 1000);
          return;
        }
        }
      const currentSpinState = spinStateRef.current as string;
      if (currentSpinState === "spinning") setSpinSync("idle");
    }, remaining);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(storeUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const commission_earned = (storeData?.commission_earned != null && parseFloat(storeData.commission_earned) >= 0)
            ? `$${parseFloat(storeData.commission_earned).toFixed(2)}`
            : "";

  const commission_rate = (storeData?.commission_rate != null)
            ? `${parseFloat(storeData.commission_rate)}%`
            : "";
            
  const commission_outstanding = (storeData?.commission_outstanding != null)
            ? `${parseFloat(storeData.commission_outstanding) < 0 ? '-' : ''}$${Math.abs(parseFloat(storeData.commission_outstanding)).toFixed(2)}`
            : null;

  const commission_paid = (storeData?.commission_paid != null)
            ? `$${parseFloat(storeData.commission_paid).toFixed(2)}`
            : null;
  
  const commission_refunded = (storeData?.commission_refunded != null)
            ? `$${parseFloat(storeData.commission_refunded).toFixed(2)}`
            : null;
            
  const hasPaymentIssue = storeData?.payment_state === "FAILED";

  const token = localStorage.getItem("token");

  if (currentUserRole === 1 || !token || error?.code === 401) {
    return null;
  }

  return (
    <>
      <div className="side-bar sidebar--shadowed">
          <>
          <div className="div-block-23">
            <StoreSelector
              onStoreChange={(store) => {
                setStoreData(store);
                setRawLogo(store?.logo ?? null);
              }}
            />
          </div>
          <div className="div-block-26-copy sidebar-profile-row">
            <StoreLogoUpload
              storeId={StoreId}
              currentLogo={rawLogo}
              storeName={StoreName}
              imgClassName="sidebar-logo-img"
              fallbackClassName="sidebar-logo-fallback"
              onLogoChange={handleLogoChange}
            />
            <h1 className="profile-name sidebar-profile-name">Hi, {currentUserName}</h1>
          </div>
          <div className="div-block-26">
            <div className="sidebar-store-link-row">
              <div className="small-test-copy">Your Store Link</div>
              <span className="sidebar-tooltip-wrap sidebar-share-btn-copy" data-tooltip={copied ? "Copied!" : "Copy store URL"}>
              <IconButton onClick={handleCopy} size="small" className="sidebar-share-btn" disableRipple={copied}>
                {copied
                  ? <DoneIcon fontSize="small" className="sidebar-icon--done" />
                  : <ContentCopyIcon fontSize="small" />
                }
              </IconButton>
            </span>
              <span className="sidebar-tooltip-wrap" data-tooltip="Share Store">
                <IconButton onClick={() => setShareOpen(true)} size="small" className="sidebar-share-btn">
                <IosShareIcon fontSize="small" />
              </IconButton>
              </span>
            </div>
            <div className="small-test sidebar-url-row">
              <a href={storeUrl} target="_blank" rel="noopener noreferrer" className="sidebar-store-url-link">{storeUrl}</a>
            </div>
          </div>

          <div className="commission-rate-text-wrapper ">
            <div className="sidebar-commission-rate-text">
              <div className="commission-rate-text">Commission Rate: {commission_rate} </div>
            </div>
          </div>
          
          
            <div className="div-block-24">
              <div className="commission-header-row">
                <div className="small-test-heading-sidebar">Congrats!</div>
                <span className="sidebar-tooltip-wrap-refresh" data-tooltip="Refresh Commission">
                  <IconButton onClick={handleRefreshCommission} size="small" disabled={spinState === "spinning"}>
                    {spinState === "done"
                      ? <DoneIcon fontSize="small" className="sidebar-icon--done" />
                      : <span
                          className={spinState === "spinning" ? "refresh-icon--spinning" : "refresh-icon--idle"}
                          style={{ display: "inline-flex" }}
                        >
                          <RefreshIcon fontSize="small" />
                        </span>
                    }
                  </IconButton>
                </span>
              </div>
            {commission_earned ? (
              <div className="commission-stats-container">
                <div className="commission-stat-item">
                  <div className="commission-stat-value">{hasPaymentIssue ? " " : commission_earned}</div>
                  <div className="commission-stat-label">{hasPaymentIssue ? " " : "earnings to date"}</div>
                </div>
                {commission_paid && (
                  <div className="commission-stat-item">
                    <div className="commission-stat-value">{hasPaymentIssue ? " " : commission_paid}</div>
                    <div className="commission-stat-label">{hasPaymentIssue ? " " : "paid"}</div>
                  </div>
                )}
                {commission_refunded && commission_refunded !== "$0.00" && (
                  <div className="commission-stat-item">
                    <div className="commission-stat-value">{hasPaymentIssue ? " " : commission_refunded}</div>
                    <div className="commission-stat-label">{hasPaymentIssue ? " " : "refunded"}</div>
                  </div>
                )}
                {commission_outstanding && (
                  <div className="commission-stat-item">
                    <div className="commission-stat-value">{hasPaymentIssue ? " " : commission_outstanding}</div>
                    <div className="commission-stat-label">{hasPaymentIssue ? " " : "balance"}</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="commission-value-row">
                <div className="small-test commission-empty-text">No commission earned yet</div>
              </div>
            )}
          </div>
          </>

        <div className="div-block-27">
          <div className="div-block-20"><AnalyticsOutlinedIcon className="image-20" />  
            {(currentUserRole==1) ? (<div><Link to={`/admin-dashboard`} className="sidebar-nav-link">Dashboard</Link></div>) : (<div><Link to={`/dashboard`} className="sidebar-nav-link">Dashboard</Link></div>)}
              
          </div>
          {(currentUserRole!=1) ?(
          <>
            <div className="div-block-20"><StorefrontOutlinedIcon className="image-20" />  
               <div><Link to={`/product-groups/${StoreId}`} className="sidebar-nav-link">Product Groups</Link></div>
            </div>
            <div className="div-block-20"><AccountBalanceWalletOutlinedIcon className="image-20" />  
               <div><Link to={`/payouts/${StoreId}`} className="sidebar-nav-link">Payouts</Link></div>
            </div>
          </>)
          : ""}
        </div>
      </div>
      <ShareStoreDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        storeUrl={storeUrl}
        storeName={StoreName}
      />
    </>
  );
};

export default DashboardSidebar;
